import { decodeBase64, encodeBase64 } from "./base64.ts";
import { MAX_DOORS_PER_LAYER } from "./doors.ts";
import { COLLISION_CELLS_PER_TILE, type DoorDefinition, type DoorLayer } from "./types.ts";
import { asArray, asJsonRecord, asRecord, asString, assertUniqueIds, sortedRecord } from "./validate.ts";

/**
 * A door layer's saved form (ADR-122 decision 2.1, document schema 12): its doors in the builder's order, each
 * `id`, `name`, `to` and key-sorted `fields`, and its cells as base64.
 */

/**
 * The first document schema that holds a door layer.
 */
export const DOOR_SCHEMA = 12;

/**
 * A door layer's plain JSON shape.
 *
 * @param layer The layer.
 * @param group The layer's group membership, to spread.
 * @returns Plain object with fixed key order.
 */
export function plainDoorLayer(layer: DoorLayer, group: { group?: string }): Record<string, unknown> {
  return {
    kind: layer.kind,
    id: layer.id,
    name: layer.name,
    ...group,
    doors: layer.doors.map((door) => ({ id: door.id, name: door.name, to: { map: door.to.map, arrival: door.to.arrival }, fields: sortedRecord(door.fields, `doors.${door.id}.fields`) })),
    cells: encodeBase64(layer.cells),
  };
}

/**
 * A string that must not be blank.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The string.
 * @throws Error naming the path when the value is not a string or is blank.
 */
function filled(value: unknown, path: string): string {
  const text = asString(value, path);
  if (text.trim() === "") {
    throw new Error(`${path} is blank`);
  }
  return text;
}

/**
 * Reads one door.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The door.
 * @throws Error naming the path when a field is missing, of the wrong type, blank, or `fields` is not a JSON record.
 */
function readDoor(value: unknown, path: string): DoorDefinition {
  const record = asRecord(value, path);
  const to = asRecord(record["to"], `${path}.to`);
  return {
    id: filled(record["id"], `${path}.id`),
    name: filled(record["name"], `${path}.name`),
    to: { map: filled(to["map"], `${path}.to.map`), arrival: filled(to["arrival"], `${path}.to.arrival`) },
    fields: asJsonRecord(record["fields"], `${path}.fields`),
  };
}

/**
 * Reads a door layer: at most `MAX_DOORS_PER_LAYER` doors of distinct ids and one byte per collision cell, each 0
 * or a 1-based place in the list. A door layer in a document below `DOOR_SCHEMA` is refused by its kind, so an
 * older build's document never holds one and this build never takes one for another kind.
 *
 * @param record The layer's record.
 * @param head The layer's id, name and group membership.
 * @param size The map's width and height in tiles, and the schema the document was stored at.
 * @param path Path for error messages.
 * @returns The layer.
 * @throws Error naming the path when the schema is too old, a door is malformed or repeated, the list is too long,
 *   the grid has the wrong length or a cell names a place beyond the list.
 */
export function readDoorLayer(record: Record<string, unknown>, head: { readonly id: string; readonly name: string; readonly membership: { group?: string } }, size: { readonly width: number; readonly height: number; readonly schemaVersion: number }, path: string): DoorLayer {
  if (size.schemaVersion < DOOR_SCHEMA) {
    throw new Error(`${path}.kind door is not a layer kind of a schema ${size.schemaVersion} document`);
  }
  const doors = asArray(record["doors"], `${path}.doors`).map((item, i) => readDoor(item, `${path}.doors[${i}]`));
  if (doors.length > MAX_DOORS_PER_LAYER) {
    throw new Error(`${path}.doors lists ${doors.length} doors; a layer holds at most ${MAX_DOORS_PER_LAYER}`);
  }
  assertUniqueIds(doors.map((door) => door.id), `${path}.doors`);
  const cells = decodeBase64(asString(record["cells"], `${path}.cells`));
  const expected = size.width * size.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE;
  if (cells.length !== expected) {
    throw new Error(`${path}.cells has ${cells.length} cells, expected ${expected}`);
  }
  cells.forEach((value, i) => {
    if (value > doors.length) {
      throw new Error(`${path}.cells[${i}] holds ${value}, beyond the layer's ${doors.length} doors`);
    }
  });
  return { kind: "door", id: head.id, name: head.name, ...head.membership, doors, cells };
}
